import { useEffect, useRef, useState } from "react";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import "./EventFormPickers.css";

/* ---- Icons ---- */
const EFCalendarIcon = () => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" style={{ flexShrink: 0 }}>
    <rect x="0.5" y="1.5" width="14" height="12" rx="2" stroke="currentColor" strokeWidth="1.3" />
    <path d="M0.5 5.5h14" stroke="currentColor" strokeWidth="1.3" />
    <path d="M4.5 0v3M10.5 0v3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
  </svg>
);
const EFClockIcon = () => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" style={{ flexShrink: 0 }}>
    <circle cx="7.5" cy="7.5" r="6.5" stroke="currentColor" strokeWidth="1.3" />
    <path d="M7.5 4v3.5L10 9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/* ---- EFDatePill: pill trigger + custom panel with Ant calendar inside ----
   Optional props (defaults keep the original behavior):
   - quickPicks=false hides the HOY / MAÑANA / ESTA SEMANA shortcuts (e.g. a birthday).
   - clearable lets "Aplicar" with an emptied date call onChange(null) instead of doing nothing.
   - disabledDate / defaultPickerValue / title are passed to the calendar and the panel header. */
export const EFDatePill = ({ value, onChange, placeholder = "dd/mm/aaaa", disabled = false, quickPicks = true, clearable = false, disabledDate, defaultPickerValue, title = "Elegir fecha" }) => {
  const [panelOpen, setPanelOpen] = useState(false);
  const [tempDate, setTempDate] = useState(null);
  const [calContainer, setCalContainer] = useState(null);
  const wrapRef = useRef(null);
  const djsVal = value ? dayjs(value) : null;
  const hasVal = djsVal && djsVal.isValid();
  useEffect(() => {
    if (panelOpen) setTempDate(hasVal ? djsVal : null);
  }, [panelOpen]); // eslint-disable-line
  useEffect(() => {
    if (!panelOpen) return;
    const handler = (e) => {
      if (wrapRef.current?.contains(e.target)) return;
      if (e.target.closest?.(".ant-picker-dropdown")) return;
      setPanelOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [panelOpen]);
  const handleApply = () => {
    if (tempDate) onChange(tempDate);
    else if (clearable && hasVal) onChange(null);
    setPanelOpen(false);
  };
  return (
    <div ref={wrapRef} className="ef-date-pill-wrap">
      <button
        type="button"
        className={`ef-date-pill-trigger${hasVal ? " has-value" : ""}`}
        onClick={() => !disabled && setPanelOpen((v) => !v)}
        disabled={disabled}
        aria-expanded={panelOpen}
      >
        <span className="ef-date-pill-label">
          {hasVal ? djsVal.format("DD/MM/YYYY") : placeholder}
        </span>
        <EFCalendarIcon />
      </button>
      {panelOpen && (
        <div className="ef-date-pill-panel">
          <div className="ef-pill-panel-head">{title}</div>
          {quickPicks && (
            <div className="ef-pill-quick-row">
              <button type="button" className="ef-pill-quick" onClick={() => setTempDate(dayjs())}>HOY</button>
              <button type="button" className="ef-pill-quick" onClick={() => setTempDate(dayjs().add(1, "day"))}>MAÑANA</button>
              <button type="button" className="ef-pill-quick" onClick={() => setTempDate(dayjs().add(7, "day"))}>ESTA SEMANA</button>
            </div>
          )}
          <div ref={setCalContainer} className="ef-pill-cal-host">
            {calContainer && (
              <DatePicker
                open={true}
                value={tempDate}
                onChange={(d) => setTempDate(d)}
                format="DD/MM/YYYY"
                allowClear={false}
                disabledDate={disabledDate}
                defaultPickerValue={defaultPickerValue}
                getPopupContainer={() => calContainer}
                popupClassName="ef-pill-cal-popup"
                className="ef-pill-hidden-input"
                inputReadOnly
              />
            )}
          </div>
          <div className="ef-pill-panel-actions">
            <button type="button" className="ef-pill-action" onClick={() => setTempDate(null)}>Limpiar</button>
            <button type="button" className="ef-pill-action" onClick={() => setPanelOpen(false)}>Cerrar</button>
            <button type="button" className="ef-pill-action ef-pill-action--primary" onClick={handleApply}>Aplicar</button>
          </div>
        </div>
      )}
    </div>
  );
};

/* ---- EFTimePill: pill trigger + scrollable time-slot panel ---- */
const EF_TIME_OPTIONS = Array.from({ length: 96 }, (_, i) => {
  const h = Math.floor(i / 4).toString().padStart(2, "0");
  const m = String((i % 4) * 15).padStart(2, "0");
  return `${h}:${m}`;
});
export const EFTimePill = ({ value, onChange, placeholder = "--:--", disabled = false }) => {
  const [panelOpen, setPanelOpen] = useState(false);
  const wrapRef = useRef(null);
  useEffect(() => {
    if (!panelOpen) return;
    const handler = (e) => {
      if (wrapRef.current?.contains(e.target)) return;
      setPanelOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [panelOpen]);
  return (
    <div ref={wrapRef} className="ef-date-pill-wrap">
      <button
        type="button"
        className={`ef-date-pill-trigger${value ? " has-value" : ""}`}
        onClick={() => !disabled && setPanelOpen((v) => !v)}
        disabled={disabled}
        aria-expanded={panelOpen}
      >
        <span className="ef-date-pill-label">{value || placeholder}</span>
        <EFClockIcon />
      </button>
      {panelOpen && (
        <div className="ef-time-pill-panel">
          <div className="ef-time-scroll">
            {EF_TIME_OPTIONS.map((t) => (
              <button
                key={t}
                type="button"
                className={`ef-time-option${value === t ? " is-active" : ""}`}
                onClick={() => { onChange(t); setPanelOpen(false); }}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
